import { Suspense, lazy, ReactNode } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { YbugProvider } from '@/lib/ybug-provider';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Eagerly load the landing page for best initial load performance
import Landing from './pages/Landing';

// Lazy load all other routes for code splitting
const Auth = lazy(() => import('./pages/Auth'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Search = lazy(() => import('./pages/Search'));
const AdminHub = lazy(() => import('./pages/AdminHub'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminFoundations = lazy(() => import('./pages/AdminFoundations'));
const Bidrag = lazy(() => import('./pages/Bidrag'));
const GrantDetail = lazy(() => import('./pages/GrantDetail'));
const GrantReview = lazy(() => import('./pages/GrantReview'));
const CreateGrant = lazy(() => import('./pages/CreateGrant'));
const Installningar = lazy(() => import('./pages/Installningar'));
const Konto = lazy(() => import('./pages/Konto'));
const Stiftelsedatabas = lazy(() => import('./pages/Stiftelsedatabas'));
const FoundationDetail = lazy(() => import('./pages/FoundationDetail'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const DataProcessingAgreement = lazy(() => import('./pages/DataProcessingAgreement'));
const NotFound = lazy(() => import('./pages/NotFound'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Per-route loader that only affects content area, not navigation
const PageLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Wrapper for lazy routes to provide per-route Suspense
const LazyRoute = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const App = () => (
  <YbugProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<LazyRoute><Auth /></LazyRoute>} />
            <Route path="/glomt-losenord" element={<LazyRoute><ForgotPassword /></LazyRoute>} />
            
            <Route path="/databehandlingsavtal" element={<LazyRoute><DataProcessingAgreement /></LazyRoute>} />
            <Route path="/privacy" element={<LazyRoute><PrivacyPolicy /></LazyRoute>} />
            <Route path="/terms" element={<LazyRoute><TermsOfService /></LazyRoute>} />

            {/* Protected routes - require authentication */}
            <Route element={<ProtectedRoute />}>
              {/* Pricing page - accessible to authenticated users without active subscription */}
              <Route path="/pricing" element={<LazyRoute><Pricing /></LazyRoute>} />
              
              <Route element={<MainLayout />}>
                <Route path="/search" element={<LazyRoute><Search /></LazyRoute>} />
                <Route path="/manage" element={<LazyRoute><Bidrag /></LazyRoute>} />
                <Route path="/manage/grant/new" element={<LazyRoute><CreateGrant /></LazyRoute>} />
                <Route path="/manage/grant/:id" element={<LazyRoute><GrantDetail /></LazyRoute>} />
                <Route path="/manage/grant/:id/review" element={<LazyRoute><GrantReview /></LazyRoute>} />
                <Route path="/installningar" element={<LazyRoute><Installningar /></LazyRoute>} />
                <Route path="/konto" element={<LazyRoute><Konto /></LazyRoute>} />
                <Route path="/stiftelsedatabas" element={<LazyRoute><Stiftelsedatabas /></LazyRoute>} />
                <Route path="/stiftelsedatabas/:orgNumber" element={<LazyRoute><FoundationDetail /></LazyRoute>} />
                <Route path="/admin" element={<LazyRoute><AdminHub /></LazyRoute>} />
                <Route path="/admin/users" element={<LazyRoute><AdminUsers /></LazyRoute>} />
                <Route path="/admin/foundations" element={<LazyRoute><AdminFoundations /></LazyRoute>} />
              </Route>
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<LazyRoute><NotFound /></LazyRoute>} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </YbugProvider>
);

export default App;
